import React, { FC } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { i18n as I18N } from 'i18next';
import { I18nextProvider } from 'react-i18next';

import { Theme, ThemeProvider } from '@chargify/custom-ui-components';
import { DeviceProvider } from '@chargify/device-provider';
import { ToastsContainer } from '@chargify/toasts';
import { DataAccess, DataAccessContext } from '@chargify/data-access';
import { SessionManager } from '@chargify/session-manager';

import SessionGuard from '../SessionGuard';
import ComponentLoader from '../ComponentLoader';

export interface ComponentOptions {
  /**
   * Internationalization instance
   */
  i18n: I18N;
  /**
   * Components theming
   */
  componentsTheme: Theme;
  /**
   * Session manager
   */
  sessionManager: SessionManager;
  /**
   * Data access interface
   */
  dataAccess: DataAccess;
}

class EmbeddableComponent {
  /** @type {I18N} */
  private i18n: I18N;

  /** @type {Theme} */
  public readonly componentsTheme: Theme;

  /** @type {Element} */
  public rootNode: Root = null;

  /** @type {SessionManager} */
  public sessionManager: SessionManager = null;

  /** @type {DataAccess} */
  public dataAccess: DataAccess = null;

  /**
   * @constructor
   * @param {string | HTMLElement} container
   */
  constructor({
    i18n,
    componentsTheme,
    dataAccess,
    sessionManager,
  }: ComponentOptions) {
    this.componentsTheme = componentsTheme;
    this.sessionManager = sessionManager;
    this.dataAccess = dataAccess;
    this.i18n = i18n;
  }

  /**
   * Get HTML element node
   * @return {Element} DOM element used to mount application.
   */
  public getMountNode(container: string | HTMLElement): Element {
    const node =
      typeof container === 'string'
        ? document.querySelector(container)
        : container;

    return node;
  }

  public destroy(): void {
    this.rootNode.unmount();
  }

  /**
   * Renders component in DOM tree
   * @param {string | HTMLElement} container - component mount DOM element
   * @param {FC} component - React component
   * @param {FC} loader - React loader component
   * @param {P} settings - component settings
   *
   * @return {void}
   */
  public renderComponent<P>(
    container: string | HTMLElement,
    component: FC<P>,
    settings: P,
    loader?: FC<P>
  ): void {
    const mountNode = this.getMountNode(container);
    this.rootNode = createRoot(mountNode);
    const {
      breakpoints: [tabletBreakpoint, desktopBreakpoint],
    } = this.componentsTheme;
    this.rootNode.render(
      <ThemeProvider theme={this.componentsTheme}>
        <DeviceProvider
          tabletMinWidth={tabletBreakpoint}
          desktopMinWidth={desktopBreakpoint}
        >
          <ToastsContainer>
            <I18nextProvider i18n={this.i18n}>
              <DataAccessContext.Provider
                value={{ dataAccess: this.dataAccess }}
              >
                <SessionGuard sessionManager={this.sessionManager}>
                  <ComponentLoader
                    component={component}
                    clientSettings={settings}
                    loader={loader}
                  />
                </SessionGuard>
              </DataAccessContext.Provider>
            </I18nextProvider>
          </ToastsContainer>
        </DeviceProvider>
      </ThemeProvider>
    );
  }
}

export default EmbeddableComponent;
